import type { ReviewStats } from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

const CATS = ['accuracy', 'value', 'cleanliness', 'location', 'check_in', 'communication'] as const
const LABEL: Record<(typeof CATS)[number], string> = {
  accuracy: 'Accuracy',
  value: 'Value',
  cleanliness: 'Cleanliness',
  location: 'Location',
  check_in: 'Check-in',
  communication: 'Communication',
}

function cellCls(v: number | undefined) {
  if (v == null) return 'text-textMuted'
  if (v >= 4.9) return 'bg-mint/15 text-mint'
  if (v >= 4.6) return 'bg-amber/15 text-amber'
  return 'bg-danger/20 text-danger font-semibold'
}

/** Property × sub-rating averages: where, exactly, the stars go missing. */
export function SubratingHeatmap({ stats }: { stats: ReviewStats }) {
  const rows = stats.properties.filter(p => Object.keys(p.subratings).length > 0)
  if (rows.length === 0) return null
  const port = stats.portfolio.subratings
  return (
    <Card>
      <CardHeader>
        <CardTitle>Where the fours and threes come from</CardTitle>
        <CardDescription>
          Sub-rating averages by property. Airbnb only reports these; the portfolio row is the
          average across every review that carried them.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-textMuted">
                <th className="px-2 py-1 text-left font-medium">Property</th>
                {CATS.map(c => (
                  <th key={c} className="px-2 py-1 text-center font-medium">
                    {LABEL[c]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/*
                The portfolio row is the benchmark every other row is read
                against, not the first entry in the list - bold text alone
                left it looking like one. It gets a banded background, larger
                numbers and a solid rule beneath it so the eye reads "baseline,
                then properties". Deliberately no extra hue: the cell colors
                already carry the only meaning color has in this table.
              */}
              <tr className="bg-surface2">
                <td className="whitespace-nowrap border-b-2 border-borderStrong px-2 py-2.5">
                  <span className="text-sm font-semibold text-text">Portfolio</span>
                  <span className="ml-2 text-[10px] uppercase tracking-wide text-textMuted">
                    all properties
                  </span>
                </td>
                {CATS.map(c => (
                  <td key={c} className="border-b-2 border-borderStrong px-1 py-2.5">
                    <div
                      className={`rounded px-1.5 py-1 text-center text-sm font-semibold tabular-nums ${cellCls(port[c])}`}
                    >
                      {port[c]?.toFixed(2) ?? '·'}
                    </div>
                  </td>
                ))}
              </tr>
              {rows.map(p => (
                <tr key={p.property_id} className="border-t border-border">
                  <td className="whitespace-nowrap px-2 py-1.5 text-text">{p.name}</td>
                  {CATS.map(c => (
                    <td key={c} className="px-1 py-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            tabIndex={0}
                            className={`cursor-default rounded px-1.5 py-1 text-center tabular-nums focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint ${cellCls(p.subratings[c])}`}
                          >
                            {p.subratings[c]?.toFixed(2) ?? '·'}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          {p.name} · {LABEL[c]}: {p.subratings[c]?.toFixed(2) ?? 'not reported'}{' '}
                          over {p.reviews} reviews
                        </TooltipContent>
                      </Tooltip>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2 flex gap-4 text-[11px] text-textMuted">
          <span>
            <i className="mr-1 inline-block h-2 w-2 rounded-sm bg-mint/60" />
            4.9 and up
          </span>
          <span>
            <i className="mr-1 inline-block h-2 w-2 rounded-sm bg-amber/60" />
            4.6 to 4.9
          </span>
          <span>
            <i className="mr-1 inline-block h-2 w-2 rounded-sm bg-danger/60" />
            below 4.6
          </span>
        </div>
      </CardContent>
    </Card>
  )
}
